import qs from 'qs';

import { paths } from '@luxuryescapes/contract-svc-payment';

import { CreditTypes, Credits, ReconciledCredits } from '~/components/Users/Credits/types';

import { CREDIT_TYPES } from '~/consts/credits';

import { PaymentsResult } from '~/types/services/payment';

import { json_headers, request } from './common';

export type RefundPreviewDestination = 'back_to_origin' | 'credit' | 'manual';

export interface RefundPreviewAccountingMetadata {
  charge_component_key: string;
  source: string;
  accounting_amount: string;
  cash_amount: string;
  refund_fee: string;
  [key: string]: unknown;
}

export interface RefundPreviewPayload {
  transaction_key: string;
  amount: string;
  refund_destination: RefundPreviewDestination;
  currency_code: string;
  brand: string;
  accounting_metadata: Array<RefundPreviewAccountingMetadata>;
  comment?: string;
  ticket_id?: string;
  reason?: string;
  additional_info?: Record<string, unknown>;
  credit_type?: string;
  bundled_credits_amount?: string;
  customer_id?: string;
  downgraded_from_item_id?: string;
  downgraded_from_item_ids?: Array<string>;
  refunder_id?: string;
  refund_recipient_id?: string;
  purchase_credit_refund?: Record<string, unknown>;
  payment_schedule_adjustment_amount?: string;
  [key: string]: unknown;
}

export interface RefundPreviewMethod {
  type: string;
  amount: number;
}

export interface RefundPreviewResult {
  totalRefundAmount: number;
  currency: string;
  refundMethods: Array<RefundPreviewMethod>;
}

function basePath() {
  return window.configs.API_HOST + '/api/';
}

function basePathV2() {
  return window.configs.API_HOST + '/api/payments/v2/';
}

function delRequest(path) {
  return request(path, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

function postRequest(path, data) {
  return request(path, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json' },
  });
}

function patchRequest(path, data) {
  return request(path, {
    method: 'PATCH',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json' },
  });
}

export function createPayPalTransaction(data) {
  return postRequest(basePath() + 'braintree/checkout', data);
}

export async function createStripeTransaction(data) {
  return postRequest(basePath() + 'stripe/checkout', data);
}

export async function createStripePaymentPlan(data) {
  return postRequest(basePathV2() + 'payment-plans', data);
}

export async function createStripePaymentIntent(data) {
  return postRequest(basePathV2() + 'stripe/payment-intents', data);
}

export async function createPendingLatitudeTransaction(data) {
  return postRequest(basePath() + 'latitude/checkout/initiate', data);
}

export async function deleteAccountingMeta(order_id, meta_id) {
  return delRequest(basePath() + 'refund_meta/' + order_id + '/meta/' + meta_id);
}

export async function createLeCreditTransaction(data) {
  return postRequest(basePath() + 'buy_with_credit/checkout', data);
}

export async function createPromoTransaction(data) {
  return postRequest(basePath() + 'promo/checkout', data);
}

export async function getPayPalCredentials() {
  const response = await request(basePath() + 'braintree/credentials');
  return response;
}

export function getStripeCredentials(currency?: string, brand?: string) {
  const query =
    currency || brand
      ? `?${new URLSearchParams({ ...(currency && { currency }), ...(brand && { brand }) }).toString()}`
      : '';
  return request(basePath() + `stripe/credentials${query}`);
}

export function getLatitudeCredentials() {
  return request(basePath() + 'latitude/credentials');
}

export function getStripeReconciliation() {
  return request(basePath() + 'reconcile/stripe');
}

export function getStripeReconciliationOrderId() {
  return request(basePath() + 'reconcile/stripe/orderids');
}

export function getStripeReconciliationCurrencies() {
  return request(basePath() + 'reconcile/stripe/currencies');
}

export function getStripeReconciliationAmounts() {
  return request(basePath() + 'reconcile/stripe/amounts');
}

export function healthCheck() {
  return request(basePath() + 'payment/health');
}

export function getCreditTypes(): CreditTypes {
  return CREDIT_TYPES;
}

export function getCredits(id_user, currency_in = 'AUD', brand, page?: number, per_page = 50): Promise<Credits> {
  let finalPage: number | undefined;
  if (page === null) {
    // if page is null, we want all options
    finalPage = undefined;
  } else if (!page) {
    // if page isn't passed at all, we want default argument of 1
    finalPage = 1;
  } else {
    // otherwise use the real value
    finalPage = page;
  }
  const queryParams = {
    id_member: id_user,
    currency: currency_in,
    brand,
    page: finalPage,
    per_page,
  };

  return request(`${basePath()}credits/credit_transactions?${qs.stringify(queryParams)}`, { method: 'GET' });
}

export async function addCredit(object) {
  const jsonData = JSON.stringify(object);
  return request(basePath() + 'credits', {
    method: 'POST',
    headers: json_headers,
    body: jsonData,
  });
}

export async function changeExpiryDate(object) {
  return postRequest(basePath() + 'credits/expires', object);
}

export async function bulkCreditsReactivation(file) {
  const data = new FormData();
  data.append('csv', file);

  return request(basePath() + 'credits/bulk/reactivate', {
    method: 'POST',
    body: data,
  });
}

export function getPaymentCurrencies() {
  return request(basePath() + 'payments/currencies', { method: 'GET' });
}

export async function getPayments(id_orders): Promise<PaymentsResult> {
  const url = `${basePath()}payments?id_orders=${id_orders}`;
  return request(url);
}

export async function previewOrderRefund(
  orderId: string,
  itemId: string,
  payload: RefundPreviewPayload,
): Promise<RefundPreviewResult> {
  const response = await postRequest(`${basePath()}refund_order/${orderId}/items/${itemId}/preview`, payload);
  return response.result;
}

export async function getMerchantFeeDetails(id_orders) {
  const url = `${basePath()}payments/v2/merchant-fees/orders/${id_orders}/details`;
  return request(url);
}

export function getRefundMeta(id_orders) {
  const url = `${basePath()}refund_meta/${id_orders}`;
  return request(url);
}

export async function getPaymentPlans(id_orders) {
  try {
    const result = await request(basePath() + `payment_plan?order_id=${id_orders}`);
    const plans = result.result.plan;
    let error_logs = [];
    for (const plan of plans) {
      const logs = await request(basePath() + `payment_errors/${plan.payment_plan_component_id}`);
      error_logs = logs.result.error_logs || [];
    }
    const out = {
      plans,
      error_logs,
    };
    return out;
  } catch (e) {
    console.warn(e);
    return { plans: [], error_logs: [] };
  }
}

export function getBraintreeReconciliation() {
  return request(basePath() + 'reconcile/braintree');
}

export function getBraintreeReconciliationOrderId() {
  return request(basePath() + 'reconcile/braintree/orderids');
}

export function getBraintreeReconciliationCurrencies() {
  return request(basePath() + 'reconcile/braintree/currencies');
}

export function getBraintreeReconciliationAmounts() {
  return request(basePath() + 'reconcile/braintree/amounts');
}

export function callDepositTriggers(action, object) {
  return postRequest(basePath() + 'stripe/deposit/trigger/' + action, object);
}

export function callInstalmentTriggers(action, object) {
  return postRequest(basePath() + 'stripe/instalment/trigger/' + action, object);
}

export function callReserveForZeroTriggers(action, object) {
  return postRequest(basePath() + 'stripe/deferred-payment/trigger/' + action, object);
}

export function paymentScheduleChargeSimulation(action, object) {
  return postRequest(basePathV2() + 'payment-schedules/trigger/' + action, object);
}

export function getDepositDetails(orderId) {
  return request(basePath() + 'payments/deposit-details/' + orderId);
}

export function getInstalmentDetails(orderId) {
  return request(basePath() + 'payments/instalment-details/' + orderId);
}

export function getReserveForZeroDetails(orderId) {
  return request(basePath() + 'payments/deferred-payment-details/' + orderId);
}

interface FeeDetails {
  type: 'percentage' | 'fixed_amount';
  value: number;
  refundable: boolean;
}
interface RetryDetails {
  max_charge_attempts: number;
  job_retry_attempts: number;
}

export enum PaymentPlanItem {
  ACCOMMODATION_TYPE = 'accommodation',
  TOUR_OFFER_TYPE = 'tour',
  TOUR_V2_OFFER_TYPE = 'tour_v2',
  TOUR_OPTIONAL_EXPERIENCE_TYPE = 'tour_optional_experience',
  SERVICE_FEE_TYPE = 'service_fee',
  FLIGHTS_ITEM_TYPE = 'flight',
  CUSTOM_OFFER_TYPE = 'custom_offer',
  INSURANCE_TYPE = 'insurance',
  CRUISE_TYPE = 'cruise',
  SUBSCRIPTION_TYPE = 'subscription',
  ADDON_TYPE = 'addon',
  EXPERIENCE_TYPE = 'experience',
  CAR_HIRE_TYPE = 'car_hire',
  BOOKING_PROTECTION_TYPE = 'booking_protection',
  PREPAID_REQUEST_TYPE = 'prepaid_request',
}

export type PaymentPlanItemType = `${PaymentPlanItem}`;

export interface Item {
  id_items: string;
  type: PaymentPlanItemType;
  total_amount?: number;
  base_amount?: number;
  fee_amount?: number;
  merchant_fee?: number;
  is_cancelled?: boolean;
}

interface ScheduledItem {
  id_items: string;
  merchant_fee: number;
  offer_type: string;
  total_amount: number;
  type: string;
}

export interface PaymentScheduleDetail {
  id: string;
  status: string;
  payment_type: string;
  total_amount: number;
  initial_total_amount: number;
  fee_amount: number;
  base_amount: number;
  due_date: string; // "2024-12-03"
  fee?: FeeDetails;
  amount_type: 'percentage' | 'fixed_amount';
  unit_of_sale?: 'per_item' | 'per_pax';
  retry: RetryDetails;
  failure_reason?: string;
  transaction_key: string;
  paid_date: string;
  items: Array<Item>;
  next_charge_date?: string;
}

interface PaymentSchedulesDetails {
  id: string;
  total_amount: number;
  initial_total_amount: number;
  total_paid: number;
  balance_amount: number;
  status: string;
  brand: string;
  currency: string;
  fk_orders: string;
  fk_customers: string;
  total_service_fee: number;
  payment_schedules: Array<PaymentScheduleDetail>;
  payment_method_id: string;
  payment_method: string;
  schedule_template_id: string;
  scheduled_items: Array<ScheduledItem>;
}

export interface PaymentScheduleDetailsResponse {
  status?: number;
  message?: string;
  result?: PaymentSchedulesDetails;
}

export interface PaymentScheduleAdjustResponse {
  status?: number;
  message?: string;
  result?: {
    eligible: boolean;
    adjusted_payment_schedule: {
      charges: Array<PaymentScheduleDetail>;
      summary: {
        pay_now_amount: number;
        schedule_amount: number;
        refund_amount: number;
      };
    };
  };
}

export function getPaymentScheduleDetails(orderId): Promise<PaymentScheduleDetailsResponse> {
  return request(basePathV2() + 'payment-schedules-state/' + orderId);
}

export type GroupPaymentContributionRole = 'lead' | 'participant';
export type GroupPaymentContributionStatus = 'pending' | 'paid' | 'cancelled' | 'amended' | 'refunded';

export interface GroupPaymentContribution {
  id: string;
  email: string;
  role: GroupPaymentContributionRole;
  status: GroupPaymentContributionStatus;
  amount: number;
  dueDate?: string | null;
  payable: boolean;
  customerId?: string;
  paymentScheduleChargeId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GroupPaymentForOrder {
  id: string;
  orderId: string;
  customerId: string;
  paymentScheduleId?: string;
  currency: string;
  createdAt: string;
  updatedAt: string;
  contributions: Array<GroupPaymentContribution>;
}

export interface GroupPaymentForOrderResponse {
  status?: number;
  message?: string;
  result?: GroupPaymentForOrder | null;
}

export function getGroupPaymentByOrderId(orderId: string): Promise<GroupPaymentForOrderResponse> {
  return request(basePathV2() + 'group-payments/orders/' + orderId);
}

export const REFUNDABLE_SOURCE_TYPE_UNPAID = 'unpaid';

export interface RefundableSourceDisplay {
  label: string;
  last4?: string;
}

export interface RefundableSource {
  type: string;
  paymentKey: string;
  customerId?: string;
  contributionId?: string;
  email?: string;
  refundableAmount: number;
  display: RefundableSourceDisplay;
}

export interface RefundableSourcesOrderLevelRemainder {
  remainingAmount: number;
}

export interface RefundableSourcesResult {
  orderId: string;
  currency: string | null;
  isGroupPayment: boolean;
  orderLevel: {
    promo: RefundableSourcesOrderLevelRemainder;
    voucher: RefundableSourcesOrderLevelRemainder;
    lePointsBenefits: RefundableSourcesOrderLevelRemainder;
  };
  sources: Array<RefundableSource>;
}

export interface RefundableSourcesResponse {
  status?: number;
  message?: string;
  result?: RefundableSourcesResult;
}

export function getRefundableSources(orderId: string): Promise<RefundableSourcesResponse> {
  return request(basePathV2() + 'orders/' + orderId + '/refundable-sources');
}

export interface PaymentScheduleItem {
  id: string;
  currency_code: string;
  reservation?: {
    check_in: string;
  };
}

export function generateNewPaymentSchedule(
  orderId: string,
  refundAmount: number,
  item: PaymentScheduleItem,
  brand: string,
): Promise<PaymentScheduleAdjustResponse> {
  return postRequest(basePathV2() + 'payment-schedules/adjust', {
    order_id: orderId,
    booking_info: {
      checkin_date: item.reservation?.check_in,
      currency: item.currency_code,
      id_items: item.id,
      adjustment_amount: -refundAmount,
    },
    brand,
  });
}

export async function getReconciledCredits(memberId, brand, currency): Promise<ReconciledCredits> {
  const url = `${basePathV2()}credit/reconcile/member/${memberId}?brand=${brand}&currency=${currency}`;
  const results = await request(url);
  if (results.status === 200) {
    return results.result;
  }
  return null;
}

export type ExpireCreditsByAmountProps = {
  brand: string;
  currency: string;
  memberId: string;
  expirationDate: string;
  creditsAdjustmentAmount: number;
  comments: string;
  creditIdsToAdjustExpiration: Array<string>;
};

export async function changeCreditExpiryByAmount(payload: ExpireCreditsByAmountProps) {
  const url = `${basePathV2()}credit/extend-expiry`;
  return postRequest(url, payload);
}

export type LinkManualStripePaymentResult =
  paths['/api/payments/v2/stripe/payment-intents/{paymentIntentId}']['patch']['responses']['200']['content']['application/json'];
export type LinkManualStripePaymentRequest =
  paths['/api/payments/v2/stripe/payment-intents/{paymentIntentId}']['patch']['requestBody']['content']['application/json'];

export interface LinkManualStripePaymentResponse {
  body: LinkManualStripePaymentResult;
}

export async function linkManualStripePayment(
  paymentIntentId: string,
  payload: LinkManualStripePaymentRequest,
): Promise<LinkManualStripePaymentResponse> {
  const url = `${basePathV2()}stripe/payment-intents/${paymentIntentId}`;
  return await patchRequest(url, payload);
}

export type GetCreditPaymentWithSourceResponse =
  paths['/api/payments/v2/credit/credit-payments/orders/{orderId}']['get']['responses']['200']['content']['application/json'];
export type CreditPaymentsWithSource = GetCreditPaymentWithSourceResponse['result']['creditPayments'];

export function getCreditPaymentsWithSource(orderId: string): Promise<GetCreditPaymentWithSourceResponse> {
  return request(basePathV2() + `credit/credit-payments/orders/${orderId}`);
}

export function getDisputeDetails(orderId) {
  return request(basePathV2() + 'payments/disputes/' + orderId);
}

export function cancelPayment(orderId: string) {
  return postRequest(basePathV2() + 'payments/cancel/' + orderId, {});
}

export function getPaymentScheduleTemplates() {
  return request(basePathV2() + 'payment-schedule-templates');
}

export function getDynamicPaymentScheduleTemplates() {
  return request(basePathV2() + 'payment-schedule-templates/dynamic');
}

type CreateCreditResponse = paths['/api/payments/v2/credit']['post']['responses']['200']['content']['application/json'];

export function createCredit(payload): Promise<CreateCreditResponse> {
  return postRequest(basePathV2() + 'credit', payload);
}

export function cancelPaymentSchedule(orderId: string, reason: string) {
  return postRequest(basePathV2() + 'payment-schedules/cancel', { orderId, reason });
}

// Bulk Credit Allocation Types
export type ItemType = 'default' | 'cruise';

export interface BulkCreditRecord {
  fk_member?: string;
  brand?: string;
  item_booking_number?: string;
  currency: string;
  amount: number;
  expires_at: string;
  comments: string;
  credit_type: string;
}

export interface FailedBulkCreditRecord extends BulkCreditRecord {
  failureReason?: string;
}

export interface ApiValidationError {
  path: string;
  value: string;
  message: string;
}

export interface BulkCreditAllocationResponse {
  status: number;
  message: string;
  result?: {
    job_id: string;
  };
  errors?: Array<string | ApiValidationError>;
}

export interface BulkCreditAllocationJobStatusResponse {
  status: number;
  message: string;
  result?: {
    jobStatus: string;
    progress: number;
    failedRecords: Array<FailedBulkCreditRecord>;
  };
}

export async function bulkCreditAllocation(
  records: Array<BulkCreditRecord>,
  itemType: ItemType = 'default',
): Promise<BulkCreditAllocationResponse> {
  return postRequest(basePathV2() + 'credit/bulk', { item_type: itemType, records });
}

interface MigratePaymentBrandPayload {
  orderId: string;
  targetBrand: string;
}

interface MigratePaymentBrandResponse {
  status: number;
  message: string;
  result: {
    paymentPlanComponentsUpdated: number;
    stripePaymentsUpdated: number;
    creditsPaymentsUpdated: number;
  };
}

export async function migratePaymentBrand(payload: MigratePaymentBrandPayload): Promise<MigratePaymentBrandResponse> {
  return postRequest(basePathV2() + 'migrate-brand', payload);
}

export async function getBulkCreditAllocationJobStatus(jobId: string): Promise<BulkCreditAllocationJobStatusResponse> {
  return request(basePathV2() + `credit/bulk/${jobId}`);
}

export type GetReversalAttemptsResponse =
  paths['/api/payments/v2/payment-reversals/{orderId}']['get']['responses'][200]['content']['application/json'];
export type ReversalAttempt = GetReversalAttemptsResponse['result'][number];

export async function getReversalAttempts(orderId: string): Promise<GetReversalAttemptsResponse> {
  return request(basePathV2() + `payment-reversals/${orderId}`);
}

/** Fixed metadata for credit secure payment links created from admin (not editable in portal). */
const CREDIT_SECURE_PAYMENT_METADATA = { itemNames: ['credit'] } as const;

export type CreateCreditSecurePaymentLinkPayload = {
  amount: number;
  currency: string;
  customerId: string;
  brand: string;
  region?: string;
  disableCreditPurchaseMerchantFees?: boolean;
  sendToMobileNumber?: string;
  smsMessageTravellerName?: string;
};

export type CreateCreditSecurePaymentLinkResponse = {
  status: number;
  message: string;
  result: {
    paymentLink: string;
  };
};

export async function createCreditSecurePaymentLink(
  payload: CreateCreditSecurePaymentLinkPayload,
): Promise<CreateCreditSecurePaymentLinkResponse> {
  const { disableCreditPurchaseMerchantFees, ...requestPayload } = payload;

  return postRequest(basePathV2() + 'secure-payments/credit', {
    ...requestPayload,
    metadata: {
      ...CREDIT_SECURE_PAYMENT_METADATA,
      ...(disableCreditPurchaseMerchantFees ? { disableCreditPurchaseMerchantFees } : {}),
    },
  });
}
